import { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MatchPublic, PredictionInput, PredictionPublic, TeamTheme } from '@mondy/shared';
import { ApiError } from '@mondy/shared/api-client';
import { api } from '@/api';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFavoriteTeamTheme } from '@/hooks/useFavoriteTeamTheme';
import { MatchCard } from '@/components/MatchCard';
import { Spinner } from '@/components/Spinner';
import { TeamFlag } from '@/components/TeamFlag';
import { TeamBackdrop } from '@/components/TeamBackdrop';
import {
  dayKey,
  formatDayHeading,
  formatKickoffDateShort,
  formatKickoffShort,
  isKickoffToday,
  matchLabel,
} from '@/lib/format';

interface MatchListResponse {
  matches: MatchPublic[];
}

function countdown(kickoffMs: number): string {
  const delta = kickoffMs - Date.now();
  if (delta <= 0) return 'any moment';
  const h = Math.floor(delta / 3_600_000);
  const m = Math.floor((delta % 3_600_000) / 60_000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

export function HomePage() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const { theme: favoriteTheme } = useFavoriteTeamTheme();

  const upcoming = useQuery({
    queryKey: ['matches', 'upcoming', 8],
    queryFn: () => api.get<MatchListResponse>('/api/matches/upcoming?limit=8'),
    refetchInterval: 30_000,
  });

  const predictionsQuery = useQuery({
    queryKey: ['predictions', 'mine'],
    queryFn: () => api.get<{ predictions: PredictionPublic[] }>('/api/predictions/mine'),
    enabled: !!user,
  });

  const predictionsByMatch = useMemo(() => {
    const map = new Map<string, PredictionPublic>();
    if (predictionsQuery.data) {
      for (const p of predictionsQuery.data.predictions) {
        map.set(p.matchId, p);
      }
    }
    return map;
  }, [predictionsQuery.data]);

  const matches = upcoming.data?.matches ?? [];
  const today = todayKey();
  const tomorrow = addDays(today, 1);

  const todayMatches = matches.filter((m) => dayKey(m.kickoffUtc) === today);
  const tomorrowMatches = matches.filter((m) => dayKey(m.kickoffUtc) === tomorrow);

  const nextMatch = matches.find((m) => m.status === 'scheduled' && !!m.team1 && !!m.team2);

  const openToday = todayMatches.filter((m) => m.status === 'scheduled').length;

  return (
    <>
      <div className="page-head">
        <div className="page-title">
          <div className="k">{formatDayHeading(new Date().toISOString())} · World Cup 2026</div>
          <h1
            style={
              favoriteTheme
                ? { fontFamily: 'var(--team-font)', letterSpacing: '0.01em' }
                : undefined
            }
          >
            {openToday > 0 ? (
              <>
                {openToday} match{openToday === 1 ? '' : 'es'} to{' '}
                <span
                  className="emph"
                  style={favoriteTheme ? { color: favoriteTheme.palette.primary } : undefined}
                >
                  predict
                </span>{' '}
                today.
              </>
            ) : todayMatches.length > 0 ? (
              <>
                The tournament is{' '}
                <span
                  className="emph"
                  style={favoriteTheme ? { color: favoriteTheme.palette.primary } : undefined}
                >
                  live
                </span>
                .
              </>
            ) : (
              <>
                Eyes on{' '}
                <span
                  className="emph"
                  style={favoriteTheme ? { color: favoriteTheme.palette.primary } : undefined}
                >
                  tomorrow
                </span>
                .
              </>
            )}
          </h1>
        </div>
        {user === undefined ? null : user ? null : (
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/login" className="btn btn-ghost">
              Log in
            </Link>
            <Link to="/signup" className="btn btn-primary">
              Sign up
            </Link>
          </div>
        )}
      </div>

      {favoriteTheme?.greeting ? (
        <div className="mobile-team-greeting">
          <div className="k">From the terraces</div>
          <div className="g">{favoriteTheme.greeting}</div>
        </div>
      ) : null}

      {upcoming.isLoading ? (
        <Spinner fullScreen label="Loading fixtures" />
      ) : upcoming.isError ? (
        <p
          style={{
            padding: '48px 0',
            textAlign: 'center',
            fontSize: 13,
            color: 'var(--danger)',
          }}
        >
          Couldn't load fixtures.
        </p>
      ) : (
        <>
          {nextMatch ? (
            <Hero
              match={nextMatch}
              prediction={predictionsByMatch.get(nextMatch.id) ?? null}
              canPredict={!!user}
              onRequireLogin={() => navigate('/login')}
              favoriteTheme={favoriteTheme}
            />
          ) : null}

          {todayMatches.length > 0 ? (
            <>
              <div className="section-head">
                <h2>
                  <span className="bar" />
                  Today
                </h2>
                <Link className="link" to="/schedule">
                  View all →
                </Link>
              </div>
              <div className="matches">
                {todayMatches.map((m) => (
                  <MatchCard key={m.id} m={m} prediction={predictionsByMatch.get(m.id)} />
                ))}
              </div>
            </>
          ) : null}

          {tomorrowMatches.length > 0 ? (
            <>
              <div className="section-head">
                <h2>
                  <span className="bar" />
                  Tomorrow
                </h2>
              </div>
              <div className="matches">
                {tomorrowMatches.map((m) => (
                  <MatchCard key={m.id} m={m} prediction={predictionsByMatch.get(m.id)} />
                ))}
              </div>
            </>
          ) : null}

          {todayMatches.length === 0 && tomorrowMatches.length === 0 ? (
            <div className="matches">
              {matches.slice(0, 5).map((m) => (
                <MatchCard key={m.id} m={m} prediction={predictionsByMatch.get(m.id)} />
              ))}
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

type Pair = { team1: number; team2: number };

function scoreEq(a: Pair | null | undefined, b: Pair | null | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.team1 === b.team1 && a.team2 === b.team2;
}

function Hero({
  match,
  prediction,
  canPredict,
  onRequireLogin,
  favoriteTheme,
}: {
  match: MatchPublic;
  prediction: PredictionPublic | null;
  canPredict: boolean;
  onRequireLogin: () => void;
  favoriteTheme: TeamTheme | null;
}) {
  const qc = useQueryClient();
  const { t1, t2 } = matchLabel(match);
  const kickoffMs = new Date(match.kickoffUtc).getTime();
  const isKnockout = match.stage !== 'group';

  const [ft, setFt] = useState<Pair>(prediction?.prediction.fullTime ?? { team1: 0, team2: 0 });
  // ET is floored at FT so the server-side cascade (ET[i] >= FT[i]) never
  // trips the moment the section becomes visible.
  const [et, setEt] = useState<Pair>(() => {
    const savedFt = prediction?.prediction.fullTime ?? { team1: 1, team2: 1 };
    const savedEt = prediction?.prediction.extraTime;
    return {
      team1: Math.max(savedEt?.team1 ?? 0, savedFt.team1),
      team2: Math.max(savedEt?.team2 ?? 0, savedFt.team2),
    };
  });
  const [pens, setPens] = useState<Pair>(
    prediction?.prediction.penalties ?? { team1: 1, team2: 0 },
  );
  const [error, setError] = useState<string | null>(null);
  const [, force] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => force((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Sync local stepper state with async prediction loads — predictions
  // arrive from a separate query and can land after the Hero has mounted.
  useEffect(() => {
    if (!prediction) return;
    const savedFt = prediction.prediction.fullTime;
    setFt(savedFt);
    const savedEt = prediction.prediction.extraTime;
    setEt({
      team1: Math.max(savedEt?.team1 ?? 0, savedFt.team1),
      team2: Math.max(savedEt?.team2 ?? 0, savedFt.team2),
    });
    const savedPens = prediction.prediction.penalties;
    if (savedPens) setPens(savedPens);
  }, [prediction]);

  const ftDraw = ft.team1 === ft.team2;
  const etDraw = et.team1 === et.team2;
  const showEt = isKnockout && ftDraw;
  const showPens = isKnockout && ftDraw && etDraw;

  const stepFt = (i: 0 | 1, d: number) =>
    setFt((p) => {
      const next: Pair = { ...p };
      if (i === 0) next.team1 = clamp(p.team1 + d);
      else next.team2 = clamp(p.team2 + d);
      // Keep ET floored at FT so the cascade stays valid the moment ET
      // becomes visible.
      setEt((prevEt) => ({
        team1: Math.max(prevEt.team1, next.team1),
        team2: Math.max(prevEt.team2, next.team2),
      }));
      return next;
    });
  const stepEt = (i: 0 | 1, d: number) =>
    setEt((p) => {
      const next: Pair = { ...p };
      // Clamp at FT floor on the way down so ET[i] >= FT[i] always holds.
      if (i === 0) next.team1 = Math.max(ft.team1, Math.min(99, p.team1 + d));
      else next.team2 = Math.max(ft.team2, Math.min(99, p.team2 + d));
      return next;
    });
  const stepPens = (i: 0 | 1, d: number) =>
    setPens((p) => {
      const next: Pair = { ...p };
      if (i === 0) next.team1 = Math.max(0, Math.min(9, p.team1 + d));
      else next.team2 = Math.max(0, Math.min(9, p.team2 + d));
      return next;
    });

  const effectiveEt = showEt ? et : null;
  const effectivePens = showPens ? pens : null;

  const unchanged =
    !!prediction &&
    prediction.prediction.fullTime.team1 === ft.team1 &&
    prediction.prediction.fullTime.team2 === ft.team2 &&
    scoreEq(prediction.prediction.extraTime, effectiveEt) &&
    scoreEq(prediction.prediction.penalties, effectivePens);

  const pensTie = showPens && pens.team1 === pens.team2;

  const save = useMutation({
    mutationFn: () => {
      const body: PredictionInput = {
        fullTime: ft,
        extraTime: effectiveEt,
        penalties: effectivePens,
      };
      return api.put<{ prediction: PredictionPublic }>(`/api/predictions/${match.id}`, body);
    },
    onSuccess: (data) => {
      setError(null);
      // Proactively seed the per-match cache so navigating to /matches/:id
      // after a Hero save finds fresh data — otherwise the match detail
      // form's local state initializes from stale cache and won't resync.
      qc.setQueryData(['prediction', match.id], { prediction: data.prediction });
      void qc.invalidateQueries({ queryKey: ['predictions', 'mine'] });
      void qc.invalidateQueries({ queryKey: ['predictions', 'all', match.id] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    },
  });

  const onSave = () => {
    if (!canPredict) {
      onRequireLogin();
      return;
    }
    if (pensTie) {
      setError('Penalties must have a winner — no ties.');
      return;
    }
    save.mutate();
  };

  // When the user has a favorite team, paint a scoped team backdrop inside
  // the hero-left pane. Also swap the hero-country font on the matching
  // side so their team's name jumps in its display font.
  const favCode = favoriteTheme?.code ?? null;
  const t1IsFav = match.team1?.fifaCode === favCode;
  const t2IsFav = match.team2?.fifaCode === favCode;
  const heroTeam = t1IsFav || t2IsFav ? favoriteTheme : null;

  return (
    <div className={heroTeam ? 'hero hero-themed' : 'hero'}>
      <div className="hero-left">
        {heroTeam ? <TeamBackdrop theme={heroTeam} intensity={0.52} /> : null}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 6,
          }}
        >
          <div className="hero-chip">
            <span
              className="live-dot"
              style={{
                background: 'var(--turf)',
                boxShadow: '0 0 0 4px var(--turf-soft)',
              }}
            />
            Next up · {formatDayHeading(match.kickoffUtc)}
            {match.group ? ` · Group ${match.group}` : ''}
          </div>
          {match.venue || match.city ? (
            <div className="hero-meta">{[match.venue, match.city].filter(Boolean).join(' · ')}</div>
          ) : null}
        </div>
        <div className="hero-match">
          <div className="hero-team">
            <HeroFlag
              fifaCode={match.team1?.fifaCode ?? null}
              fallbackFlagUrl={match.team1?.flagUrl ?? null}
              teamId={match.team1?.id ?? null}
            />
            <div
              className="hero-country"
              style={
                t1IsFav && favoriteTheme
                  ? {
                      fontFamily: 'var(--team-font)',
                      color: favoriteTheme.palette.primary,
                    }
                  : undefined
              }
            >
              {t1}
            </div>
            {match.team1?.fifaCode ? <div className="code">{match.team1.fifaCode}</div> : null}
          </div>
          <div className="hero-center">
            <div className="vs-label">
              {isKickoffToday(match.kickoffUtc)
                ? 'Kick-off'
                : formatKickoffDateShort(match.kickoffUtc)}
            </div>
            <div className="vs-kickoff">{formatKickoffShort(match.kickoffUtc)}</div>
            <div className="vs-label" style={{ marginTop: 8, color: 'var(--hot)' }}>
              in {countdown(kickoffMs)}
            </div>
          </div>
          <div className="hero-team">
            <HeroFlag
              fifaCode={match.team2?.fifaCode ?? null}
              fallbackFlagUrl={match.team2?.flagUrl ?? null}
              teamId={match.team2?.id ?? null}
            />
            <div
              className="hero-country"
              style={
                t2IsFav && favoriteTheme
                  ? {
                      fontFamily: 'var(--team-font)',
                      color: favoriteTheme.palette.primary,
                    }
                  : undefined
              }
            >
              {t2}
            </div>
            {match.team2?.fifaCode ? <div className="code">{match.team2.fifaCode}</div> : null}
          </div>
        </div>
      </div>

      <div className="hero-right">
        <div className="pl">Quick pick</div>
        <h3>
          {match.team1?.fifaCode ?? '?'} · {match.team2?.fifaCode ?? '?'}
        </h3>
        <HeroStepperPair
          value={ft}
          onStep={stepFt}
          cap1={match.team1?.fifaCode ?? '—'}
          cap2={match.team2?.fifaCode ?? '—'}
          disabled={!canPredict || save.isPending}
        />
        {showEt ? (
          <>
            <div className="pl">Extra time</div>
            <HeroStepperPair
              value={et}
              onStep={stepEt}
              cap1={match.team1?.fifaCode ?? '—'}
              cap2={match.team2?.fifaCode ?? '—'}
              disabled={!canPredict || save.isPending}
            />
          </>
        ) : null}
        {showPens ? (
          <>
            <div className="pl">Penalties · no ties</div>
            <HeroStepperPair
              value={pens}
              onStep={stepPens}
              cap1={match.team1?.fifaCode ?? '—'}
              cap2={match.team2?.fifaCode ?? '—'}
              disabled={!canPredict || save.isPending}
            />
          </>
        ) : null}
        {error ? <p style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</p> : null}
        <button
          type="button"
          className="btn btn-primary btn-full"
          disabled={canPredict && (save.isPending || unchanged || pensTie)}
          onClick={onSave}
        >
          {save.isPending
            ? 'Saving…'
            : !canPredict
              ? 'Log in to predict'
              : pensTie
                ? 'Pens need a winner'
                : unchanged
                  ? `Saved · ${ft.team1}–${ft.team2}`
                  : prediction
                    ? `Update · ${ft.team1}–${ft.team2}`
                    : 'Lock it in →'}
        </button>
      </div>
    </div>
  );
}

function HeroStepperPair({
  value,
  onStep,
  cap1,
  cap2,
  disabled,
}: {
  value: Pair;
  onStep: (i: 0 | 1, d: number) => void;
  cap1: string;
  cap2: string;
  disabled: boolean;
}) {
  return (
    <div className="mini-predict">
      <div className="stepper">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onStep(0, -1)}
          aria-label="decrease"
        >
          −
        </button>
        <div className="stacked">
          <div className="val">{value.team1}</div>
          <div className="cap">{cap1}</div>
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onStep(0, 1)}
          aria-label="increase"
        >
          +
        </button>
      </div>
      <div className="dash">–</div>
      <div className="stepper">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onStep(1, -1)}
          aria-label="decrease"
        >
          −
        </button>
        <div className="stacked">
          <div className="val">{value.team2}</div>
          <div className="cap">{cap2}</div>
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onStep(1, 1)}
          aria-label="increase"
        >
          +
        </button>
      </div>
    </div>
  );
}

/**
 * Large native flag glyph for the Home hero. The design reference uses
 * waving national flag glyphs here, not flat pattern tiles.
 */
function HeroFlag({
  fifaCode,
  fallbackFlagUrl,
  teamId,
}: {
  fifaCode: string | null;
  fallbackFlagUrl: string | null;
  teamId: string | null;
}) {
  const flag = (
    <TeamFlag
      code={fifaCode}
      url={fallbackFlagUrl}
      size="md"
      className="hero-flag-tile"
    />
  );
  if (!teamId) return flag;
  // `display: contents` keeps the flag tile a direct flex child of
  // `.hero-team` so column gap/alignment are unchanged; cursor inherits
  // to the span so the whole tile reads as clickable.
  return (
    <Link
      to={`/teams/${teamId}`}
      aria-label={`View ${fifaCode ?? 'team'} squad`}
      style={{ display: 'contents', cursor: 'pointer' }}
    >
      {flag}
    </Link>
  );
}

function clamp(n: number): number {
  return Math.max(0, Math.min(99, n));
}

function todayKey(): string {
  return dayKey(new Date().toISOString());
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return dayKey(d.toISOString());
}
